import React from 'react';
import { CheckIcon, InfoIcon, LockKeyholeIcon, SparklesIcon, XIcon } from 'lucide-react';
import { PractitionerFlowShell } from '../components/practitioners/PractitionerFlowShell';
import { AccountForm } from '../components/practitioners/AccountForm';
import { MembershipPlans } from '../components/practitioners/MembershipPlans';
import { ResumeSignIn } from '../components/practitioners/ResumeSignIn';
import { usePractitionerSignup } from '../hooks/usePractitionerSignup';
import { foundingMembershipMessage, foundingPlans } from '../data/foundingOffer';
import type { SignupStep } from '../types/practitionerSignup';
interface PractitionerJoinPageProps {
  initialStep: SignupStep;
}
export function PractitionerJoinPage({
  initialStep
}: PractitionerJoinPageProps) {
  const flow = usePractitionerSignup(initialStep);
  const selectedPlan = foundingPlans.find(p => p.id === flow.paymentModalPlan);
  const selectedPlanName = foundingPlans.find(p => p.id === flow.selectedPlan)?.name ?? null;
  if (flow.step === 'account') {
    const emailExists = flow.accountError?.code === 'email_exists';
    return <PractitionerFlowShell currentStep={0} data-id="element-652" data-mp="2d5fbdym1dukq">
        <div className="max-w-xl mx-auto px-6" data-id="element-653" data-mp="2d689kb6xjbr9">
          <h1 className="font-serif text-4xl md:text-5xl text-charcoal leading-[1.1] tracking-tight text-center" data-id="element-654" data-mp="287drjbgsdr87">
            Create your practitioner account
          </h1>

          <p className="mt-5 text-lg text-charcoal/70 font-light leading-relaxed text-center" data-id="element-655" data-mp="35s9pmds9fxpr">
            Join Fem Entity and become part
            of Canada's national women's
            health network. Create your
            account, choose your membership,
            then complete your practitioner
            profile and verification in the
            Fem Entity app.
          </p>

          <div className="mt-10 rounded-3xl border border-purple-200 bg-white px-6 py-8 md:px-9 md:py-9 shadow-[0_18px_50px_-30px_rgba(44,44,44,0.5)]" data-id="element-656" data-mp="1keyd8602zoqq">
            <AccountForm submitting={flow.accountSubmitting} serverError={flow.accountError?.message ?? null} serverErrorAction={emailExists ? <button type="button" onClick={() => flow.goTo('membership')} className="font-semibold text-red-900 underline underline-offset-2 rounded focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-purple-600" data-id="element-658" data-mp="1cwqpfb67vuxn">
                    Sign in to choose your
                    membership
                  </button> : null} onSubmit={flow.submitAccount} data-id="element-657" data-mp="1hy6b95g7y5h3" />
          </div>

          <p className="mt-6 text-center text-sm text-charcoal/65 leading-relaxed" data-id="element-659" data-mp="kaxj4rlppy64">
            Already have a Fem Entity
            practitioner account? Sign in
            through the{' '}
            <a href="/get-the-app" className="font-medium text-purple-700 underline underline-offset-2 decoration-purple-300 hover:text-purple-900" data-id="element-660" data-mp="3st6nf07g1qvr">
              Fem Entity app
            </a>
            .
          </p>
        </div>
      </PractitionerFlowShell>;
  }
  return <PractitionerFlowShell currentStep={1} data-id="element-661" data-mp="fktrdiuv7a79">
      <div className="max-w-7xl mx-auto px-6 md:px-12" data-id="element-662" data-mp="fjsgbedumnpj">
        <div className="max-w-2xl mx-auto text-center" data-id="element-663" data-mp="tbqggzpl199k">
          <h1 className="font-serif text-4xl md:text-5xl text-charcoal leading-[1.1] tracking-tight" data-id="element-664" data-mp="tb6y5hw0siv0">
            Choose your Fem Entity
            membership
          </h1>

          <p className="mt-6 inline-flex items-start gap-2.5 rounded-2xl bg-purple-800 px-5 py-4 text-left text-base text-white leading-relaxed" data-id="element-665" data-mp="13b7in75ml50s">
            <SparklesIcon aria-hidden="true" className="mt-1 w-4 h-4 shrink-0 text-purple-200" data-id="element-666" data-mp="y98hm3xcyk70" />
            {foundingMembershipMessage}
          </p>
        </div>

        <div className="max-w-3xl mx-auto mt-8 space-y-4" data-id="element-667" data-mp="1r5hoim6cjcfw">
          {flow.checkoutCancelled && !flow.needsSignIn && <p role="status" className="flex items-start gap-2.5 rounded-xl border border-purple-200 bg-purple-50 px-4 py-3 text-sm text-charcoal/80" data-id="element-668" data-mp="1w4uehfsikzmp">
                <InfoIcon aria-hidden="true" className="mt-0.5 w-4 h-4 shrink-0 text-purple-700" data-id="element-669" data-mp="13gygm7r7l1oc" />
                Checkout was cancelled.
                You haven't been charged.
                Choose a membership whenever
                you're ready.
              </p>}

          {flow.checkoutError && <p role="alert" className="rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-800" data-id="element-670" data-mp="2oszx9x00cejw">
              {flow.checkoutError}
            </p>}

          {flow.needsSignIn && <ResumeSignIn planName={selectedPlanName} submitting={flow.signInSubmitting} error={flow.signInError} onSubmit={flow.resumeWithSignIn} onCancel={flow.cancelSignIn} data-id="element-671" data-mp="87lg36mpavp6" />}
        </div>

        <div className="mt-12" data-id="element-672" data-mp="3gmd74bz9ri4z">
          <MembershipPlans selectedPlan={flow.selectedPlan} redirectingPlan={flow.redirectingPlan} onChoose={flow.choosePlan} data-id="element-673" data-mp="3gnelapbl0kqx" />
        </div>

        <p className="mt-10 max-w-2xl mx-auto text-center text-sm text-charcoal/60 leading-relaxed" data-id="element-674" data-mp="19vnlljx0wzes">
          Payment details are entered
          securely on Stripe. Fem Entity
          does not see or store your card
          information.
        </p>
      </div>

      {/* ------------------------------------------------
          FEM ENTITY PAYMENT CONFIRMATION MODAL
       ------------------------------------------------- */}

      {selectedPlan && <div className="fixed inset-0 z-50 flex items-center justify-center bg-charcoal/50 px-5 py-8" role="dialog" aria-modal="true" aria-labelledby="membership-modal-title" data-id="element-675" data-mp="2zb7wwzxalf80">
          <div className="relative w-full max-w-lg overflow-hidden rounded-3xl bg-white shadow-2xl" data-id="element-676" data-mp="3e2gv3xf9g8wr">
            <button type="button" onClick={flow.cancelPaymentModal} disabled={flow.redirectingPlan !== null} aria-label="Close" className="absolute right-5 top-5 rounded-full p-2 text-charcoal/50 hover:bg-purple-50 hover:text-charcoal focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-purple-600 disabled:opacity-40" data-id="element-677" data-mp="3i3iq22259ysx">
              <XIcon aria-hidden="true" className="h-5 w-5" data-id="element-678" data-mp="293a83092o3ek" />
            </button>

            <div className="px-7 py-8 md:px-9 md:py-10" data-id="element-679" data-mp="243xmeitxt2dk">
              <div className="flex h-12 w-12 items-center justify-center rounded-full bg-purple-100" data-id="element-680" data-mp="242ctexn9trht">
                <LockKeyholeIcon aria-hidden="true" className="h-5 w-5 text-purple-700" data-id="element-681" data-mp="1gca0ygfg2jio" />
              </div>

              <h2 id="membership-modal-title" className="mt-6 font-serif text-3xl text-charcoal" data-id="element-682" data-mp="1q57nqyrgyun3">
                Confirm your membership
              </h2>

              <p className="mt-3 text-charcoal/65 leading-relaxed" data-id="element-683" data-mp="xmkb87qjgnxm">
                You're joining Fem Entity
                as a founding member.
              </p>

              <div className="mt-7 rounded-2xl border border-purple-200 bg-purple-50 px-5 py-5" data-id="element-684" data-mp="76ime8qemcwe">
                <div className="flex items-center justify-between gap-4" data-id="element-685" data-mp="c40lz8ij7frq">
                  <span className="font-serif text-2xl text-charcoal" data-id="element-686" data-mp="h3wt3gmiznbi">
                    {selectedPlan.name}
                  </span>

                  <span className="font-serif text-xl text-purple-800" data-id="element-687" data-mp="l6jk5fbdh47n">
                    {selectedPlan.price}
                    <span className="text-sm" data-id="element-688" data-mp="q41yjl85sbn1">
                      {selectedPlan.priceSuffix}
                    </span>
                  </span>
                </div>

                <p className="mt-2 text-sm text-charcoal/60" data-id="element-689" data-mp="zu4o1bg2n7gm">
                  Founding rate, from month
                  three
                </p>
              </div>

              <div className="mt-6 space-y-3" data-id="element-690" data-mp="18v2vk3ppxx3y">
                <div className="flex items-start gap-3" data-id="element-691" data-mp="18wdxrz3imwsy">
                  <CheckIcon aria-hidden="true" className="mt-0.5 h-5 w-5 shrink-0 text-purple-700" data-id="element-692" data-mp="1dtw9tyctxs56" />
                  <div data-id="element-693" data-mp="2ydhvzojy4lwx">
                    <p className="font-semibold text-charcoal" data-id="element-694" data-mp="2y9u38rmbs3j9">
                      $0 due today
                    </p>
                    <p className="text-sm text-charcoal/60" data-id="element-695" data-mp="2pb07t4kqdj7a">
                      Your first two months
                      are complimentary.
                    </p>
                  </div>
                </div>

                <div className="flex items-start gap-3" data-id="element-696" data-mp="3h2xyghf6yofz">
                  <CheckIcon aria-hidden="true" className="mt-0.5 h-5 w-5 shrink-0 text-purple-700" data-id="element-697" data-mp="3h4irg2luxzbq" />
                  <div data-id="element-698" data-mp="38931hwo844bf">
                    <p className="font-semibold text-charcoal" data-id="element-699" data-mp="3889t6r9m96r6">
                      Payment method required
                    </p>
                    <p className="text-sm text-charcoal/60" data-id="element-700" data-mp="4l163cua46d4">
                      Stripe securely saves
                      your payment method for
                      billing after the trial.
                    </p>
                  </div>
                </div>

                <div className="flex items-start gap-3" data-id="element-701" data-mp="z74etj7nc5s5">
                  <CheckIcon aria-hidden="true" className="mt-0.5 h-5 w-5 shrink-0 text-purple-700" data-id="element-702" data-mp="u6yrgf8et5fv" />
                  <div data-id="element-703" data-mp="1444jpqsrf2ho">
                    <p className="font-semibold text-charcoal" data-id="element-704" data-mp="143v32ttlu7on">
                      Your founding rate is
                      locked in
                    </p>
                    <p className="text-sm text-charcoal/60" data-id="element-705" data-mp="1i1jdt8tyjyfz">
                      Your selected rate begins
                      in month three while your
                      membership remains active.
                    </p>
                  </div>
                </div>
              </div>

              {flow.checkoutError && <p role="alert" className="mt-5 rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-800" data-id="element-706" data-mp="20tlyqwdmu0w1">
                  {flow.checkoutError}
                </p>}

              <button type="button" onClick={flow.continueToPayment} disabled={flow.redirectingPlan !== null} className="mt-8 w-full inline-flex items-center justify-center rounded-full bg-purple-700 px-6 py-4 text-sm font-semibold text-white transition-colors hover:bg-purple-800 disabled:cursor-not-allowed disabled:opacity-60 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-purple-600 focus-visible:ring-offset-2" data-id="element-707" data-mp="2fezpejzl0g4g">
                {flow.redirectingPlan ? 'Opening secure payment…' : 'Continue to secure payment'}
              </button>

              <p className="mt-4 text-center text-xs text-charcoal/50" data-id="element-708" data-mp="1du316zrnca00">
                You'll enter your card details
                securely on Stripe.
              </p>
            </div>
          </div>
        </div>}
    </PractitionerFlowShell>;
}